import { useEffect, useState } from 'react'
import type { User } from 'firebase/auth'
import {
  DayAggregate,
  TIME_BUCKET_COUNT,
  achievementFor,
  getFirebaseApp,
  globalBaselineAggregate,
  hasEnoughToday,
  percentileBeaten,
  timeBucket,
} from '../../lib/leaderboard'
import { solutionIndex, getWordOfDay, unicodeSplit } from '../../lib/words'
import { getGuessStatuses } from '../../lib/statuses'
import { HARDEST_WORD_LOOKBACK_DAYS } from '../../constants/settings'

type ReviewWord = { word: string; count: number }

type Play = {
  id: string
  day: number
  guesses: number
  solved: boolean
  hard: boolean
  duration: number
  words: string[]
  invalid: string[]
  ts: Date | null
}

type State =
  | { phase: 'init' }
  | { phase: 'signedOut' }
  | { phase: 'error'; code: string; message: string }
  | { phase: 'denied'; email: string; uid: string }
  | { phase: 'loading'; email: string }
  | { phase: 'ready'; email: string; plays: Play[]; review: ReviewWord[] }

const TZ = 'Europe/Vilnius'
const vilnius = new Intl.DateTimeFormat('lt-LT', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
})
const fmtTime = (t: Date | null) => (t ? vilnius.format(t) : '—')

// Group label = the Vilnius calendar date of that game day's plays.
const vilniusDate = new Intl.DateTimeFormat('lt-LT', {
  timeZone: TZ,
  month: '2-digit',
  day: '2-digit',
})

// "YYYY-MM" bucket key in the game's timezone, for the accumulation timeline.
const vilniusMonth = new Intl.DateTimeFormat('lt-LT', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
})
const monthKey = (ms: number) => {
  const parts = vilniusMonth.formatToParts(new Date(ms))
  const y = parts.find((p) => p.type === 'year')?.value ?? '????'
  const m = parts.find((p) => p.type === 'month')?.value ?? '??'
  return `${y}-${m}`
}

type TimelineMonth = {
  month: string
  newWords: number // distinct words first seen this month
  submissions: number // total invalid submissions this month
  cumulative: number // running distinct total
}

const fmtDur = (s: number) =>
  s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '—'

// One guessed word as mini Wordle tiles, evaluated against that day's
// solution with the game's own status logic.
const MiniWord = ({ word, solution }: { word: string; solution: string }) => {
  const statuses = getGuessStatuses(solution, word)
  return (
    <span className="inline-flex align-middle">
      {unicodeSplit(word).map((ch, i) => (
        <span
          key={i}
          className={
            'w-3 h-3.5 flex items-center justify-center text-[8px] font-semibold uppercase ' +
            (statuses[i] === 'correct'
              ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-100'
              : statuses[i] === 'present'
              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
              : 'bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-400')
          }
        >
          {ch}
        </span>
      ))}
    </span>
  )
}

export const AdminView = () => {
  const [state, setState] = useState<State>({ phase: 'init' })
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [backfilling, setBackfilling] = useState(false)
  const [deleting, setDeleting] = useState(false)
  // Words the admin opts OUT of deletion (the real words to keep). Everything
  // else in the queue is selected for deletion by default.
  const [kept, setKept] = useState<Set<string>>(new Set())

  // JEV (TypeSafe) coarse-sieve review. Key kept client-side; calls go through
  // the same-origin Apache proxy /jev/ to dodge CORS.
  const [jevKey, setJevKey] = useState<string>(() => {
    try {
      return localStorage.getItem('jevApiKey') || ''
    } catch {
      return ''
    }
  })
  const [showKey, setShowKey] = useState(false)
  const [jevResults, setJevResults] = useState<
    Record<string, { choice: string; conf: number; netinka: number }>
  >({})
  const [jevRunning, setJevRunning] = useState(false)
  const [jevProgress, setJevProgress] = useState(0)
  // Shared deletion threshold for whichever model is sieving.
  const [jevThreshold, setJevThreshold] = useState(0.7)

  // Haiku (Anthropic) second-opinion sieve. Key kept client-side. The Anthropic
  // API allows direct browser calls when the dangerous-direct-browser-access
  // header is set, so — unlike JEV — no reverse proxy is needed.
  const [haikuKey, setHaikuKey] = useState<string>(() => {
    try {
      return localStorage.getItem('anthropicApiKey') || ''
    } catch {
      return ''
    }
  })
  const [showHaikuKey, setShowHaikuKey] = useState(false)
  const [haikuResults, setHaikuResults] = useState<
    Record<string, { choice: string; conf: number; netinka: number }>
  >({})
  const [haikuRunning, setHaikuRunning] = useState(false)
  const [haikuProgress, setHaikuProgress] = useState(0)
  // Sonnet (Anthropic) "advanced" sieve — same key + direct-browser path as
  // Haiku, just a stronger model. Cheapest tier above Haiku; clearly better
  // keep/drop accuracy, so it's the recommended mode when precision matters.
  const [sonnetResults, setSonnetResults] = useState<
    Record<string, { choice: string; conf: number; netinka: number }>
  >({})
  const [sonnetRunning, setSonnetRunning] = useState(false)
  const [sonnetProgress, setSonnetProgress] = useState(0)
  // Which model's scores drive the sort + deletion threshold.
  const [sieve, setSieve] = useState<'jev' | 'haiku' | 'sonnet'>('jev')

  // Accumulation timeline. Separate from the 10-day play window above — it reads
  // the full results/ collection on demand (heavy), so it's behind a button.
  const [timeline, setTimeline] = useState<TimelineMonth[] | null>(null)
  const [timelineLoading, setTimelineLoading] = useState(false)
  const [timelineTotal, setTimelineTotal] = useState(0)

  // Rough API cost per word (USD), measured earlier this session:
  // JEV ~$0.09 / 6103, Haiku (batched) ~$0.30 / 6103. Sonnet is ~3× Haiku
  // (input $3/M vs $1/M, output $15/M vs $5/M), so ~$0.00015/word.
  const JEV_PER_WORD = 0.000015
  const HAIKU_PER_WORD = 0.00005
  const SONNET_PER_WORD = 0.00015
  const fmtCost = (n: number) =>
    n < 0.001 ? '<$0.001' : '$' + n.toFixed(n < 0.1 ? 3 : 2)

  const copyAll = (words: string[]) => {
    navigator.clipboard
      .writeText(words.join(' '))
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => {})
  }

  // One-time: replay the invalid arrays already stored in result documents
  // into the review queue. Idempotent — only adds each word's shortfall, so
  // running twice is a no-op.
  const backfill = async () => {
    if (state.phase !== 'ready' || backfilling) return
    setBackfilling(true)
    try {
      const desired = new Map<string, number>()
      state.plays.forEach((pl) =>
        pl.invalid.forEach((w) => {
          const id = w.toLocaleLowerCase('lt')
          desired.set(id, (desired.get(id) ?? 0) + 1)
        })
      )
      const current = new Map(state.review.map((r) => [r.word, r.count]))
      const ops: string[] = []
      desired.forEach((n, w) => {
        for (let i = 0; i < n - (current.get(w) ?? 0); i++) ops.push(w)
      })
      const app = await getFirebaseApp()
      const { getFirestore, doc, setDoc, increment, collection, getDocs } =
        await import('firebase/firestore')
      const db = getFirestore(app)
      for (let i = 0; i < ops.length; i += 40) {
        await Promise.all(
          ops
            .slice(i, i + 40)
            .map((w) =>
              setDoc(
                doc(db, 'invalidWords', w),
                { word: w, count: increment(1) },
                { merge: true }
              )
            )
        )
      }
      const snap = await getDocs(collection(db, 'invalidWords'))
      const review: ReviewWord[] = snap.docs
        .map((d) => ({ word: d.data().word, count: d.data().count ?? 0 }))
        .sort((a, b) => b.count - a.count)
      setState((prev) => (prev.phase === 'ready' ? { ...prev, review } : prev))
    } catch (e) {
      console.warn('admin: backfill failed', e)
    } finally {
      setBackfilling(false)
    }
  }

  // Builds the accumulation timeline from the WHOLE results/ history (not just
  // the 10-day window). Per month: how many distinct unrecognised words were
  // first seen (newWords), total submissions, and a running distinct total.
  const loadTimeline = async () => {
    if (state.phase !== 'ready' || timelineLoading) return
    setTimelineLoading(true)
    try {
      const app = await getFirebaseApp()
      const { getFirestore, collection, getDocs } = await import(
        'firebase/firestore'
      )
      const db = getFirestore(app)
      const snap = await getDocs(collection(db, 'results'))
      const firstSeen = new Map<string, number>() // word -> earliest ts (ms)
      const submissionsByMonth = new Map<string, number>()
      snap.docs.forEach((d) => {
        const v = d.data() as {
          ts?: { toDate?: () => Date }
          invalid?: string[]
        }
        const ms = v.ts?.toDate ? v.ts.toDate().getTime() : null
        if (!ms) return
        const month = monthKey(ms)
        const inv = v.invalid ?? []
        submissionsByMonth.set(
          month,
          (submissionsByMonth.get(month) ?? 0) + inv.length
        )
        inv.forEach((w) => {
          const id = w.toLocaleLowerCase('lt')
          const prev = firstSeen.get(id)
          if (prev == null || ms < prev) firstSeen.set(id, ms)
        })
      })
      const newByMonth = new Map<string, number>()
      firstSeen.forEach((ms) => {
        const month = monthKey(ms)
        newByMonth.set(month, (newByMonth.get(month) ?? 0) + 1)
      })
      const months = Array.from(
        new Set(
          Array.from(submissionsByMonth.keys()).concat(
            Array.from(newByMonth.keys())
          )
        )
      ).sort()
      let cum = 0
      const rows: TimelineMonth[] = months.map((m) => {
        const newWords = newByMonth.get(m) ?? 0
        cum += newWords
        return {
          month: m,
          newWords,
          submissions: submissionsByMonth.get(m) ?? 0,
          cumulative: cum,
        }
      })
      setTimeline(rows)
      setTimelineTotal(firstSeen.size)
    } catch (e) {
      console.warn('admin: timeline load failed', e)
    } finally {
      setTimelineLoading(false)
    }
  }

  // Removes a word from the review queue only. Individual play records keep
  // their invalid history — nothing in results/ is ever deleted.
  const toggleKeep = (word: string) =>
    setKept((prev) => {
      const next = new Set(prev)
      next.has(word) ? next.delete(word) : next.add(word)
      return next
    })

  const saveJevKey = (k: string) => {
    setJevKey(k)
    try {
      localStorage.setItem('jevApiKey', k)
    } catch {}
  }

  const saveHaikuKey = (k: string) => {
    setHaikuKey(k)
    try {
      localStorage.setItem('anthropicApiKey', k)
    } catch {}
  }

  // One JEV classification via the same-origin proxy. Coarse sieve: we care
  // mainly about the NETINKA probability as a "garbage score".
  const jevClassify = async (word: string, count: number | null) => {
    const r = await fetch('/jev/systemone', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${jevKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'jev-latest',
        state: `Lietuviško Wordle žaidėjas įvedė 5 raidžių spėjimą "${word}" (įvesta ${
          count ?? '?'
        } kartų), bet sistema jo neatpažino.`,
        questions: {
          verdict: {
            type: 'choice',
            instructions:
              'Ar tai tinkamas lietuviškas spėjimas, vertas žodyno?',
            criteria: {
              TINKA: 'tikras lietuviškas žodis ar jo įprasta forma',
              ANT_RIBOS: 'abejotina, reikia žmogaus peržiūros',
              NETINKA:
                'akivaizdus šlamštas: atsitiktinės raidės, neegzistuojantis žodis, beprasmis raidžių rinkinys, svetimybė ar žargonas',
            },
          },
        },
      }),
    })
    if (!r.ok) throw new Error('JEV ' + r.status)
    const v = (await r.json()).answers?.verdict
    return {
      choice: v?.choice ?? 'ERR',
      conf: v?.confidence ?? 0,
      netinka: v?.probabilities?.NETINKA ?? 0,
    }
  }

  // Runs JEV over the whole queue, then auto-marks for deletion everything
  // whose NETINKA score is at or above the threshold (the "definite garbage").
  const runJevReview = async () => {
    if (state.phase !== 'ready' || jevRunning || !jevKey) return
    setJevRunning(true)
    setJevProgress(0)
    const list = state.review.map((r) => ({ w: r.word, c: r.count }))
    const results: Record<
      string,
      { choice: string; conf: number; netinka: number }
    > = {}
    let done = 0
    // HTTP/2 lets the browser multiplex well beyond the ~6 HTTP/1.1 cap.
    const CONC = 15
    try {
      for (let i = 0; i < list.length; i += CONC) {
        await Promise.all(
          list.slice(i, i + CONC).map(async ({ w, c }) => {
            try {
              results[w] = await jevClassify(w, c ?? null)
            } catch {
              results[w] = { choice: 'ERR', conf: 0, netinka: 0 }
            }
          })
        )
        done += Math.min(CONC, list.length - i)
        setJevProgress(done)
      }
      setJevResults(results)
      setSieve('jev')
      setKept(
        new Set(
          list
            .filter(({ w }) => (results[w]?.netinka ?? 0) < jevThreshold)
            .map((x) => x.w)
        )
      )
    } catch (e) {
      console.warn('admin: jev review failed', e)
    } finally {
      setJevRunning(false)
    }
  }

  // One batched Claude classification (Haiku or Sonnet — same prompt, same
  // Anthropic key). Claude returns a self-reported confidence; we fold
  // verdict+confidence into the same NETINKA "garbage score" JEV produces, so
  // the slider/sort machinery is shared across all three models.
  const anthropicClassifyBatch = async (
    batch: { w: string; c: number | null }[],
    model: string
  ) => {
    const sys =
      'Esi lietuvių kalbos ekspertas. Vertini žaidimo atmestus 5 raidžių „žodžius“: ' +
      'ar tai taisyklingas lietuviškas spėjimas, vertas žodyno.\n' +
      'TINKA: egzistuojantis bendrinės lietuvių kalbos žodis teisinga forma — vns./dgs. ' +
      'vardininkas, bendratis, būtojo l. 3 asm., bet kurios giminės būdvardis (gražu, graži, baugi), ' +
      'žodis su priešdėliu (atūžė), įteisintas skolinys (biuras, super), žinomas tikrinis vardas ' +
      '(balis, jalta, kamiu, norfa, škėma), žinoma pastovi forma (paštu, širdy, delne, minty).\n' +
      'ANT_RIBOS: abejotina — reta/šalutinė linksnio forma vesta tik dėl 5 raidžių, nežinomas ' +
      'tikrinis vardas, ribinis skolinys.\n' +
      'NETINKA: GRIEŽTAI atmesk gramatikos/rašybos klaidas (vešti vietoj vežti, kylti, mėšti), ' +
      'neegzistuojančius jokia forma, į žodį panašius bet beprasmius (šelta, daubo), barbarizmus/' +
      'žargoną/rusicizmus (kolis, bodis, nafik), atsitiktines raides. Abejoji dėl formos → NETINKA.'
    const listing = batch
      .map(({ w, c }) => `${w} (įvesta ${c ?? '?'}×)`)
      .join('\n')
    const r = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'x-api-key': haikuKey,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true',
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        model,
        max_tokens: 4000,
        system: [
          { type: 'text', text: sys, cache_control: { type: 'ephemeral' } },
        ],
        messages: [
          {
            role: 'user',
            content:
              `Klasifikuok šiuos ${batch.length} žodžius. Grąžink TIK JSON masyvą ` +
              `[{"word","verdict":"TINKA|ANT_RIBOS|NETINKA","confidence":0..1}], be jokio kito teksto:\n` +
              listing,
          },
        ],
      }),
    })
    if (!r.ok) throw new Error(model + ' ' + r.status)
    const data = await r.json()
    const text: string = (data.content ?? [])
      .filter((b: { type?: string }) => b.type === 'text')
      .map((b: { text?: string }) => b.text ?? '')
      .join('')
    const m = text.match(/\[[\s\S]*\]/)
    const arr: Array<{ word?: string; verdict?: string; confidence?: number }> =
      m ? JSON.parse(m[0]) : []
    const out: Record<
      string,
      { choice: string; conf: number; netinka: number }
    > = {}
    for (const o of arr) {
      const choice = o.verdict ?? 'ERR'
      const conf = typeof o.confidence === 'number' ? o.confidence : 0.5
      const netinka =
        choice === 'NETINKA' ? conf : choice === 'TINKA' ? 1 - conf : 0.5
      out[String(o.word ?? '').toLocaleLowerCase('lt')] = {
        choice,
        conf,
        netinka,
      }
    }
    return out
  }

  // Runs a Claude model over the whole queue in batches (cheap: ~40
  // words/request), then auto-marks for deletion everything at or above the
  // threshold. Shared by the Haiku and Sonnet (advanced) sieves.
  const runAnthropicReview = async (
    model: string,
    which: 'haiku' | 'sonnet',
    running: boolean,
    setRunning: (b: boolean) => void,
    setProgress: (n: number) => void,
    setResults: (
      r: Record<string, { choice: string; conf: number; netinka: number }>
    ) => void
  ) => {
    if (state.phase !== 'ready' || running || !haikuKey) return
    setRunning(true)
    setProgress(0)
    const list = state.review.map((r) => ({ w: r.word, c: r.count }))
    const results: Record<
      string,
      { choice: string; conf: number; netinka: number }
    > = {}
    const BATCH = 40
    const CONC = 4
    const chunks: { w: string; c: number | null }[][] = []
    for (let i = 0; i < list.length; i += BATCH)
      chunks.push(list.slice(i, i + BATCH))
    try {
      for (let i = 0; i < chunks.length; i += CONC) {
        await Promise.all(
          chunks.slice(i, i + CONC).map(async (batch) => {
            try {
              const got = await anthropicClassifyBatch(batch, model)
              batch.forEach(({ w }) => {
                results[w] = got[w] ?? { choice: 'ERR', conf: 0, netinka: 0 }
              })
            } catch {
              batch.forEach(({ w }) => {
                results[w] = { choice: 'ERR', conf: 0, netinka: 0 }
              })
            }
          })
        )
        setProgress(Math.min((i + CONC) * BATCH, list.length))
      }
      setResults(results)
      setSieve(which)
      setKept(
        new Set(
          list
            .filter(({ w }) => (results[w]?.netinka ?? 0) < jevThreshold)
            .map((x) => x.w)
        )
      )
    } catch (e) {
      console.warn('admin: ' + which + ' review failed', e)
    } finally {
      setRunning(false)
    }
  }

  const runHaikuReview = () =>
    runAnthropicReview(
      'claude-haiku-5-5',
      'haiku',
      haikuRunning,
      setHaikuRunning,
      setHaikuProgress,
      setHaikuResults
    )

  const runSonnetReview = () =>
    runAnthropicReview(
      'claude-sonnet-5-5',
      'sonnet',
      sonnetRunning,
      setSonnetRunning,
      setSonnetProgress,
      setSonnetResults
    )

  // Slider: re-mark deletion so only NETINKA score >= t is selected, using
  // whichever model is currently sieving.
  const applyThreshold = (t: number) => {
    setJevThreshold(t)
    const res =
      sieve === 'haiku'
        ? haikuResults
        : sieve === 'sonnet'
        ? sonnetResults
        : jevResults
    if (state.phase === 'ready' && Object.keys(res).length) {
      setKept(
        new Set(
          state.review
            .map((r) => r.word)
            .filter((w) => (res[w]?.netinka ?? 0) < t)
        )
      )
    }
  }

  // Re-sieve with a different model's scores (when several have run).
  const switchSieve = (which: 'jev' | 'haiku' | 'sonnet') => {
    setSieve(which)
    const res =
      which === 'haiku'
        ? haikuResults
        : which === 'sonnet'
        ? sonnetResults
        : jevResults
    if (state.phase === 'ready' && Object.keys(res).length) {
      setKept(
        new Set(
          state.review
            .map((r) => r.word)
            .filter((w) => (res[w]?.netinka ?? 0) < jevThreshold)
        )
      )
    }
  }

  // Mass-delete every queued word not opted out of deletion.
  const deleteSelected = async () => {
    if (state.phase !== 'ready' || deleting) return
    const toDelete = state.review.map((r) => r.word).filter((w) => !kept.has(w))
    if (toDelete.length === 0) return
    setDeleting(true)
    try {
      const app = await getFirebaseApp()
      const { getFirestore, writeBatch, doc } = await import(
        'firebase/firestore'
      )
      const db = getFirestore(app)
      for (let i = 0; i < toDelete.length; i += 450) {
        const batch = writeBatch(db)
        toDelete
          .slice(i, i + 450)
          .forEach((w) => batch.delete(doc(db, 'invalidWords', w)))
        await batch.commit()
      }
      const gone = new Set(toDelete)
      setState((prev) =>
        prev.phase === 'ready'
          ? { ...prev, review: prev.review.filter((r) => !gone.has(r.word)) }
          : prev
      )
    } catch (e) {
      console.warn('admin: mass delete failed', e)
    } finally {
      setDeleting(false)
    }
  }

  const load = async (user: User) => {
    setState({ phase: 'loading', email: user.email ?? '?' })
    try {
      const app = await getFirebaseApp()
      const { getFirestore, collection, query, where, getDocs } = await import(
        'firebase/firestore'
      )
      const db = getFirestore(app)
      const snap = await getDocs(
        query(
          collection(db, 'results'),
          where('day', '>=', solutionIndex - HARDEST_WORD_LOOKBACK_DAYS)
        )
      )
      const plays: Play[] = snap.docs.map((d) => {
        const v = d.data()
        return {
          id: d.id,
          day: v.day,
          guesses: v.guesses,
          solved: v.solved,
          hard: v.hard ?? false,
          duration: v.duration ?? 0,
          words: v.words ?? [],
          invalid: v.invalid ?? [],
          ts: v.ts ? v.ts.toDate() : null,
        }
      })
      plays.sort((a, b) => (b.ts?.getTime() ?? 0) - (a.ts?.getTime() ?? 0))
      // Review queue is secondary — never let its read failure lock the admin
      // out (e.g. before the invalidWords rules are published).
      let review: ReviewWord[] = []
      try {
        const wordsSnap = await getDocs(collection(db, 'invalidWords'))
        review = wordsSnap.docs
          .map((d) => ({ word: d.data().word, count: d.data().count ?? 0 }))
          .sort((a, b) => b.count - a.count)
      } catch (e) {
        console.warn(
          'admin: review queue read failed (rules not published?)',
          e
        )
      }
      setState({ phase: 'ready', email: user.email ?? '?', plays, review })
    } catch (e: any) {
      if (e?.code === 'permission-denied') {
        setState({ phase: 'denied', email: user.email ?? '?', uid: user.uid })
      } else {
        setState({
          phase: 'error',
          code: e?.code ?? 'unknown',
          message: String(e?.message ?? e),
        })
      }
    }
  }

  // Restore a persisted Google session on refresh instead of asking again.
  useEffect(() => {
    let done = false
    getFirebaseApp().then(async (app) => {
      const {
        getAuth,
        setPersistence,
        browserLocalPersistence,
        onAuthStateChanged,
      } = await import('firebase/auth')
      const auth = getAuth(app)
      try {
        await setPersistence(auth, browserLocalPersistence)
      } catch (e) {}
      onAuthStateChanged(auth, (user) => {
        if (done) return
        done = true
        if (user && !user.isAnonymous) {
          load(user)
        } else {
          setState({ phase: 'signedOut' })
        }
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const signIn = async () => {
    try {
      const app = await getFirebaseApp()
      const {
        getAuth,
        setPersistence,
        browserLocalPersistence,
        GoogleAuthProvider,
        signInWithPopup,
      } = await import('firebase/auth')
      const auth = getAuth(app)
      try {
        await setPersistence(auth, browserLocalPersistence)
      } catch (e) {}
      const cred = await signInWithPopup(auth, new GoogleAuthProvider())
      load(cred.user)
    } catch (e: any) {
      setState({
        phase: 'error',
        code: e?.code ?? 'unknown',
        message: String(e?.message ?? e),
      })
    }
  }

  const signOutAdmin = async () => {
    const app = await getFirebaseApp()
    const { getAuth, signOut } = await import('firebase/auth')
    await signOut(getAuth(app))
    setState({ phase: 'signedOut' })
  }

  const Shell = ({ children }: { children: React.ReactNode }) => (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900">
      <header className="bg-indigo-600 text-white px-4 py-3 shadow flex items-center justify-between">
        <h1 className="text-lg font-bold">Žodelė · administravimas</h1>
        {state.phase === 'ready' && (
          <button
            onClick={signOutAdmin}
            className="text-xs bg-indigo-500 hover:bg-indigo-400 rounded px-3 py-1"
          >
            Atsijungti
          </button>
        )}
      </header>
      <main className="p-4 max-w-5xl mx-auto text-slate-800 dark:text-slate-100">
        {children}
      </main>
    </div>
  )

  if (state.phase === 'init') {
    return (
      <Shell>
        <p className="text-slate-400">Kraunama…</p>
      </Shell>
    )
  }

  if (state.phase === 'signedOut') {
    return (
      <Shell>
        <div className="mt-10 flex flex-col items-center">
          <p className="mb-4 text-slate-500">
            Prisijunkite savininko Google paskyra.
          </p>
          <button
            onClick={signIn}
            className="rounded-md shadow px-5 py-2 bg-indigo-600 text-white font-medium hover:bg-indigo-700"
          >
            Prisijungti su Google
          </button>
        </div>
      </Shell>
    )
  }

  if (state.phase === 'loading') {
    return (
      <Shell>
        <p className="text-slate-400">Kraunami duomenys ({state.email})…</p>
      </Shell>
    )
  }

  if (state.phase === 'error') {
    return (
      <Shell>
        <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950 dark:border-red-800 p-4">
          <p className="font-bold text-red-700 dark:text-red-300">
            Klaida: {state.code}
          </p>
          <p className="mt-1 text-sm text-red-600 dark:text-red-400 break-all">
            {state.message}
          </p>
          {state.code === 'auth/unauthorized-domain' && (
            <p className="mt-2 text-sm">
              Pridėkite šį domeną: Firebase Console → Authentication → Settings
              → Authorized domains.
            </p>
          )}
          <button
            onClick={signIn}
            className="mt-3 rounded-md px-4 py-2 bg-indigo-600 text-white text-sm hover:bg-indigo-700"
          >
            Bandyti dar kartą
          </button>
        </div>
      </Shell>
    )
  }

  if (state.phase === 'denied') {
    return (
      <Shell>
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950 dark:border-amber-800 p-4">
          <p className="font-bold text-amber-700 dark:text-amber-300">
            Prieiga uždrausta
          </p>
          <p className="mt-2 text-sm">
            Prisijungta kaip <code className="font-mono">{state.email}</code>{' '}
            (uid <code className="font-mono">{state.uid}</code>).
          </p>
          <p className="mt-1 text-sm text-slate-500">
            Jei tai jūsų paskyra — įrašykite šį el. paštą į firestore.rules
            (isAdmin) ir paskelbkite taisykles iš naujo.
          </p>
          <button
            onClick={signOutAdmin}
            className="mt-3 rounded-md px-4 py-2 bg-slate-600 text-white text-sm hover:bg-slate-700"
          >
            Atsijungti
          </button>
        </div>
      </Shell>
    )
  }

  const { plays, review } = state
  const solved = plays.filter((p) => p.solved).length

  // Rough per-play placement: rank each play against its own day's loaded
  // plays (or the global baseline when the day is thin) using the exact
  // production formula. Feeling over precision.
  const dayAggs = new Map<number, DayAggregate>()
  plays.forEach((p) => {
    let a = dayAggs.get(p.day)
    if (!a) {
      a = {
        day: p.day,
        solution: '',
        total: 0,
        failed: 0,
        guessCounts: [0, 0, 0, 0, 0, 0],
        timeBuckets: Array.from({ length: 6 }, () =>
          Array(TIME_BUCKET_COUNT).fill(0)
        ),
      }
      dayAggs.set(p.day, a)
    }
    a.total += 1
    if (!p.solved) {
      a.failed += 1
    } else {
      a.guessCounts[p.guesses - 1] += 1
      const b = timeBucket(p.duration)
      if (b !== null) {
        a.timeBuckets[p.guesses - 1][b] += 1
      }
    }
  })
  const prize = (p: Play) => {
    if (!p.solved) return null
    const dayAgg = dayAggs.get(p.day) ?? null
    const source = hasEnoughToday(dayAgg) ? dayAgg! : globalBaselineAggregate()
    const pct = percentileBeaten(source, p.guesses, true, p.duration)
    return pct === null ? null : { pct, emoji: achievementFor(pct).emoji }
  }
  const invalid = review
  const selectedForDeletion = invalid.filter((r) => !kept.has(r.word)).length
  const hasJev = Object.keys(jevResults).length > 0
  const hasHaiku = Object.keys(haikuResults).length > 0
  const hasSonnet = Object.keys(sonnetResults).length > 0
  const activeResults =
    sieve === 'haiku'
      ? haikuResults
      : sieve === 'sonnet'
      ? sonnetResults
      : jevResults
  const hasActive = Object.keys(activeResults).length > 0
  const sieveLabel =
    sieve === 'haiku' ? 'Haiku' : sieve === 'sonnet' ? 'Sonnet' : 'JEV'
  const sieveCount = [hasJev, hasHaiku, hasSonnet].filter(Boolean).length
  // After a review, show most-likely-garbage first (by the sieving model).
  const sortedInvalid = hasActive
    ? [...invalid].sort(
        (a, b) =>
          (activeResults[b.word]?.netinka ?? -1) -
          (activeResults[a.word]?.netinka ?? -1)
      )
    : invalid
  // Group the day navigation by Vilnius calendar date so a single date is
  // one entry, even if two game-day indices happen to share it. Plays are
  // already sorted newest-first, so first-seen order is newest-first.
  const dateOf = (p: Play) => (p.ts ? vilniusDate.format(p.ts) : 'be datos')
  const dates = Array.from(new Set(plays.map(dateOf)))
  const activeDate = selectedDate ?? dates[0] ?? null
  const activeIdx = dates.indexOf(activeDate as string)
  const dayPlays = plays.filter((p) => dateOf(p) === activeDate)

  const Stat = ({
    label,
    value,
  }: {
    label: string
    value: string | number
  }) => (
    <div className="rounded-lg bg-white dark:bg-slate-800 shadow px-4 py-3">
      <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
        {value}
      </div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  )

  return (
    <Shell>
      <p className="mb-3 text-sm text-slate-500">
        {state.email} · paskutinės {HARDEST_WORD_LOOKBACK_DAYS} d.
      </p>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <Stat label="žaidimų" value={plays.length} />
        <Stat
          label="atspėta"
          value={
            plays.length ? `${Math.round((100 * solved) / plays.length)}%` : '—'
          }
        />
        <Stat label="nerastų žodžių" value={invalid.length} />
      </div>

      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1 flex-wrap">
          <h2 className="font-bold">Kaupimosi istorija (pagal mėnesį)</h2>
          <button
            onClick={loadTimeline}
            disabled={timelineLoading}
            className="text-xs rounded px-2.5 py-1 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
          >
            {timelineLoading
              ? 'Kraunama…'
              : timeline
              ? 'Atnaujinti'
              : 'Rodyti istoriją'}
          </button>
          {timeline && (
            <span className="text-xs text-slate-500">
              viso skirtingų žodžių: {timelineTotal}
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 mb-2">
          Nauji (pirmą kartą pamatyti) neatpažinti žodžiai per mėnesį ir bendras
          kaupimasis. Skaito visą „results/“ kolekciją, todėl gali būti lėta.
        </p>
        {timeline &&
          (timeline.length === 0 ? (
            <p className="text-sm text-slate-500">Nėra duomenų.</p>
          ) : (
            <div className="rounded-lg bg-slate-100 dark:bg-slate-800 p-3 text-sm">
              {(() => {
                const max = Math.max(...timeline.map((t) => t.newWords), 1)
                return timeline.map((t) => (
                  <div
                    key={t.month}
                    title={`${t.submissions} submisijų`}
                    className="grid items-center gap-2 mb-1"
                    style={{ gridTemplateColumns: '66px 1fr 132px' }}
                  >
                    <span className="text-slate-500 tabular-nums">
                      {t.month}
                    </span>
                    <span className="block bg-slate-200 dark:bg-slate-700 rounded h-5 overflow-hidden">
                      <span
                        className="block h-full bg-indigo-500"
                        style={{ width: `${(t.newWords / max) * 100}%` }}
                      />
                    </span>
                    <span className="text-slate-500 tabular-nums text-right">
                      +{t.newWords}
                      <span className="text-slate-400"> · Σ{t.cumulative}</span>
                    </span>
                  </div>
                ))
              })()}
              <p className="text-xs text-slate-400 mt-2">
                +N = naujų žodžių tą mėnesį · Σ = bendras kaupimasis · užvedus
                rodoma submisijų tą mėnesį
              </p>
            </div>
          ))}
      </div>

      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <h2 className="font-bold">Neatpažinti žodžiai („Žodis nerastas“)</h2>
        {invalid.length > 0 && (
          <>
            <button
              onClick={deleteSelected}
              disabled={deleting || selectedForDeletion === 0}
              className="text-xs rounded px-2.5 py-1 bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
            >
              {deleting
                ? 'Trinama…'
                : `Ištrinti pažymėtus (${selectedForDeletion})`}
            </button>
            <button
              onClick={() => setKept(new Set(invalid.map((r) => r.word)))}
              disabled={kept.size === invalid.length}
              className="text-xs rounded px-2.5 py-1 bg-slate-500 text-white hover:bg-slate-600 disabled:opacity-40"
            >
              Atžymėti visus
            </button>
            <button
              onClick={() => setKept(new Set())}
              disabled={kept.size === 0}
              className="text-xs rounded px-2.5 py-1 bg-slate-500 text-white hover:bg-slate-600 disabled:opacity-40"
            >
              Pažymėti visus
            </button>
            <button
              onClick={() =>
                copyAll(invalid.map((r) => r.word).filter((w) => kept.has(w)))
              }
              disabled={kept.size === 0}
              className="text-xs rounded px-2.5 py-1 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
            >
              {copied ? 'Nukopijuota ✓' : `Kopijuoti paliktus (${kept.size})`}
            </button>
          </>
        )}
        <button
          onClick={backfill}
          disabled={backfilling}
          className="text-xs rounded px-2.5 py-1 bg-slate-600 text-white hover:bg-slate-700 disabled:opacity-40"
        >
          {backfilling ? 'Pildoma…' : 'Užpildyti iš senų įrašų'}
        </button>
      </div>
      {invalid.length > 0 && (
        <p className="text-xs text-slate-500 mb-2">
          Pažymėti žodžiai bus ištrinti. Atžymėkite tuos, kuriuos paliekate
          (pridedami į žodyną). Arba paleisk JEV peržiūrą grubiam sijojimui.
        </p>
      )}

      {invalid.length > 0 && (
        <div className="rounded-lg bg-slate-100 dark:bg-slate-800 p-3 mb-3 text-sm">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={runJevReview}
              disabled={jevRunning || haikuRunning || sonnetRunning || !jevKey}
              className="rounded px-3 py-1 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
            >
              {jevRunning
                ? `JEV… ${jevProgress}/${invalid.length}`
                : 'JEV peržiūra'}
            </button>
            <button
              onClick={runHaikuReview}
              disabled={
                haikuRunning || jevRunning || sonnetRunning || !haikuKey
              }
              className="rounded px-3 py-1 bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40"
            >
              {haikuRunning
                ? `Haiku… ${haikuProgress}/${invalid.length}`
                : 'Haiku peržiūra'}
            </button>
            <button
              onClick={runSonnetReview}
              disabled={
                sonnetRunning || jevRunning || haikuRunning || !haikuKey
              }
              className="rounded px-3 py-1 bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40"
              title="Pažangus režimas: tiksliausias (po Opus), ~3× brangiau nei Haiku"
            >
              {sonnetRunning
                ? `Sonnet… ${sonnetProgress}/${invalid.length}`
                : 'Sonnet (pažangus)'}
            </button>
            <button
              onClick={() => setShowKey((s) => !s)}
              className="text-xs text-slate-500 underline"
            >
              {jevKey ? 'JEV raktas ✓' : 'Įvesti JEV raktą'}
            </button>
            <button
              onClick={() => setShowHaikuKey((s) => !s)}
              className="text-xs text-slate-500 underline"
            >
              {haikuKey ? 'Claude raktas ✓' : 'Įvesti Claude raktą'}
            </button>
          </div>

          <div className="text-xs text-slate-500 mt-1">
            {invalid.length} ž. įvertinimo kaina ≈ JEV{' '}
            {fmtCost(invalid.length * JEV_PER_WORD)} · Haiku{' '}
            {fmtCost(invalid.length * HAIKU_PER_WORD)} · Sonnet{' '}
            {fmtCost(invalid.length * SONNET_PER_WORD)}
          </div>

          {showKey && (
            <div className="flex items-center gap-2 mt-2">
              <input
                type="password"
                defaultValue={jevKey}
                placeholder="JEV API raktas (saugomas tik naršyklėje)"
                onChange={(e) => saveJevKey(e.target.value.trim())}
                className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs font-mono"
              />
            </div>
          )}

          {showHaikuKey && (
            <div className="flex items-center gap-2 mt-2">
              <input
                type="password"
                defaultValue={haikuKey}
                placeholder="Anthropic API raktas (sk-ant-…, saugomas tik naršyklėje)"
                onChange={(e) => saveHaikuKey(e.target.value.trim())}
                className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs font-mono"
              />
            </div>
          )}

          {hasActive && (
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {sieveCount > 1 && (
                <div className="flex items-center gap-1 text-xs">
                  <span className="text-slate-500">sijoti pagal:</span>
                  {hasJev && (
                    <button
                      onClick={() => switchSieve('jev')}
                      className={
                        'rounded px-2 py-0.5 ' +
                        (sieve === 'jev'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-slate-200 dark:bg-slate-700')
                      }
                    >
                      JEV
                    </button>
                  )}
                  {hasHaiku && (
                    <button
                      onClick={() => switchSieve('haiku')}
                      className={
                        'rounded px-2 py-0.5 ' +
                        (sieve === 'haiku'
                          ? 'bg-amber-600 text-white'
                          : 'bg-slate-200 dark:bg-slate-700')
                      }
                    >
                      Haiku
                    </button>
                  )}
                  {hasSonnet && (
                    <button
                      onClick={() => switchSieve('sonnet')}
                      className={
                        'rounded px-2 py-0.5 ' +
                        (sieve === 'sonnet'
                          ? 'bg-violet-600 text-white'
                          : 'bg-slate-200 dark:bg-slate-700')
                      }
                    >
                      Sonnet
                    </button>
                  )}
                </div>
              )}
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round(jevThreshold * 100)}
                onChange={(e) => applyThreshold(Number(e.target.value) / 100)}
                className="flex-1 min-w-[8rem]"
              />
              <span className="text-xs font-mono w-40 text-right">
                {sieveLabel} NETINKA ≥ {Math.round(jevThreshold * 100)}% →
                trinti ({selectedForDeletion})
              </span>
            </div>
          )}
        </div>
      )}

      {invalid.length === 0 ? (
        <p className="text-slate-400 mb-6">nėra</p>
      ) : (
        <div className="flex flex-wrap gap-2 mb-6">
          {sortedInvalid.map(({ word: w, count: n }) => {
            const willDelete = !kept.has(w)
            const j = jevResults[w]
            const h = haikuResults[w]
            const s = sonnetResults[w]
            return (
              <button
                key={w}
                onClick={() => toggleKeep(w)}
                title={
                  (willDelete ? 'Bus ištrintas' : 'Paliekamas') +
                  (j
                    ? ` · JEV: ${j.choice} (NETINKA ${Math.round(
                        j.netinka * 100
                      )}%)`
                    : '') +
                  (h
                    ? ` · Haiku: ${h.choice} (NETINKA ${Math.round(
                        h.netinka * 100
                      )}%)`
                    : '') +
                  (s
                    ? ` · Sonnet: ${s.choice} (NETINKA ${Math.round(
                        s.netinka * 100
                      )}%)`
                    : '')
                }
                className={
                  'inline-flex items-center gap-1 rounded-full pl-2 pr-2.5 py-1 text-sm uppercase select-none ' +
                  (willDelete
                    ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 ring-1 ring-red-300 dark:ring-red-700'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 line-through')
                }
              >
                <span className="not-italic no-underline">
                  {willDelete ? '☑' : '☐'}
                </span>
                {w}
                {j && (
                  <span
                    className="text-[10px] font-bold no-underline text-emerald-700 dark:text-emerald-400"
                    title="JEV NETINKA tikimybė"
                  >
                    J{Math.round(j.netinka * 100)}
                  </span>
                )}
                {h && (
                  <span
                    className="text-[10px] font-bold no-underline text-amber-700 dark:text-amber-400"
                    title="Haiku NETINKA tikimybė"
                  >
                    H{Math.round(h.netinka * 100)}
                  </span>
                )}
                {s && (
                  <span
                    className="text-[10px] font-bold no-underline text-violet-700 dark:text-violet-400"
                    title="Sonnet NETINKA tikimybė"
                  >
                    S{Math.round(s.netinka * 100)}
                  </span>
                )}
                <span
                  className={
                    'text-xs font-bold rounded-full px-1.5 no-underline ' +
                    (willDelete
                      ? 'bg-red-200 dark:bg-red-800'
                      : 'bg-slate-200 dark:bg-slate-600')
                  }
                >
                  {n}
                </span>
              </button>
            )
          })}
        </div>
      )}

      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h2 className="font-bold">Žaidimai</h2>
        {dates.length > 0 && activeDate !== null && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedDate(dates[activeIdx + 1])}
              disabled={activeIdx >= dates.length - 1}
              className="rounded px-2 py-1 text-sm bg-white dark:bg-slate-800 shadow disabled:opacity-30 hover:bg-indigo-100 dark:hover:bg-slate-700"
            >
              ‹ senesnė
            </button>
            <span className="text-sm font-medium min-w-[6rem] text-center">
              {activeDate}{' '}
              <span className="text-slate-400">({dayPlays.length})</span>
            </span>
            <button
              onClick={() => setSelectedDate(dates[activeIdx - 1])}
              disabled={activeIdx <= 0}
              className="rounded px-2 py-1 text-sm bg-white dark:bg-slate-800 shadow disabled:opacity-30 hover:bg-indigo-100 dark:hover:bg-slate-700"
            >
              naujesnė ›
            </button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto overflow-y-auto max-h-[65vh] rounded-lg shadow">
        <table className="w-full text-sm bg-white dark:bg-slate-800">
          <thead className="bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 sticky top-0 z-10">
            <tr className="text-left">
              <th className="px-3 py-2">laikas</th>
              <th className="px-3 py-2">diena</th>
              <th className="px-3 py-2">sp.</th>
              <th className="px-3 py-2">rezult.</th>
              <th className="px-3 py-2">vieta</th>
              <th className="px-3 py-2">rež.</th>
              <th className="px-3 py-2">trukmė</th>
              <th className="px-3 py-2">žodžiai</th>
              <th className="px-3 py-2">nerasti</th>
            </tr>
          </thead>
          <tbody>
            {dayPlays.map((p, i) => (
              <tr
                key={p.id}
                className={
                  i % 2
                    ? 'bg-slate-50 dark:bg-slate-800/50'
                    : 'bg-white dark:bg-slate-800'
                }
              >
                <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
                  {fmtTime(p.ts)}
                </td>
                <td className="px-3 py-2">{p.day}</td>
                <td className="px-3 py-2 font-bold">{p.guesses}</td>
                <td className="px-3 py-2">
                  <span
                    className={
                      p.solved
                        ? 'rounded px-2 py-0.5 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'rounded px-2 py-0.5 text-xs bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }
                  >
                    {p.solved ? 'atspėta' : 'ne'}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {prize(p) ? (
                    <span>
                      {prize(p)!.emoji}{' '}
                      <span className="text-xs text-slate-500">
                        {prize(p)!.pct}%
                      </span>
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2" title={p.hard ? 'pasunkintas' : ''}>
                  {p.hard ? '💪' : ''}
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {fmtDur(p.duration)}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
                    {p.words.map((w, wi) => (
                      <span key={wi} className="flex items-center gap-x-1">
                        {wi > 0 && <span className="text-slate-400">-</span>}
                        <MiniWord word={w} solution={getWordOfDay(p.day)} />
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-amber-700 dark:text-amber-400">
                  {p.invalid.join(' ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Shell>
  )
}
